import { 
  Box, 
  Button, 
  Grid, 
  TextField, 
  Typography,
  Fade,
  useTheme,
  useMediaQuery
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { useForm, Controller } from "react-hook-form";
import api from "../../api/client";
import { Email, ArrowBack } from "@mui/icons-material";

// Background images matching the login screen
const backgroundImages = [
  "https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80",
  "https://images.unsplash.com/photo-1501504905252-473c47e087f8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80",
  "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
  "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
];

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  // Background image carousel effect
  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setCurrentBgIndex((prev) => (prev + 1) % backgroundImages.length);
        setFadeIn(true);
      }, 200);
    }, 8000);

    // Trigger initial animation
    setTimeout(() => setIsLoaded(true), 100);

    return () => clearInterval(interval);
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        email: data?.email,
      };
      const res = await api.post("ForgotPassword", payload);
      if (res?.data?.status === "success") {
        showSnackbar("success", res.data.message);

        const expiryTime = new Date(Date.now() + 15 * 60 * 1000).toISOString();
        localStorage.setItem("otpExpiryTime", expiryTime);
        localStorage.setItem("resetEmail", payload.email);

        navigate("/reset-password", {
          state: { email: payload.email, otpExpiryTime: expiryTime },
        });
      } else {
        showSnackbar("error", res?.data?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Error during forgot password:", error);
      showSnackbar("error", "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/");
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "black",
      }}
    >
      {/* Background Image Carousel */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        <Fade in={fadeIn} timeout={500}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url(${backgroundImages[currentBgIndex]})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              position: "absolute",
            }}
          />
        </Fade>
        
        {/* Gradient overlay for better text readability */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
          }}
        />
      </Box>

      {/* Content */}
      <Grid
        container
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          minHeight: "100vh",
          px: { xs: 2, md: 8 },
          py: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Left Side - Branding & Info */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            color: "white",
            pr: { md: 4 },
            minHeight: "100vh",
            transform: isLoaded ? "translateX(0)" : "translateX(-50px)",
            opacity: isLoaded ? 1 : 0,
            transition: "all 0.8s ease-out",
            transitionDelay: "0.2s",
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 6 }}>
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              mb: 2,
              transform: isLoaded ? "translateY(0)" : "translateY(-20px)",
              opacity: isLoaded ? 1 : 0,
              transition: "all 0.6s ease-out",
              transitionDelay: "0.4s",
            }}>
              <Typography
                sx={{ 
                  fontSize: "32px", 
                  fontWeight: "bold", 
                  color: "white",
                }}
              >
                Talent IQ
              </Typography>
            </Box>
            <Typography sx={{ 
              color: "rgba(255,255,255,0.8)", 
              fontWeight: 400,
              transform: isLoaded ? "translateY(0)" : "translateY(10px)",
              opacity: isLoaded ? 1 : 0,
              transition: "all 0.6s ease-out",
              transitionDelay: "0.6s",
            }}>
              Professional Assessment Platform
            </Typography>
          </Box>

          {/* Main Heading */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{ 
                fontSize: { xs: "40px", md: "52px" }, 
                fontWeight: "bold",
                lineHeight: 1.1,
                mb: 1,
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.7s ease-out",
                transitionDelay: "0.3s",
              }}
            >
              Reset Your
            </Typography>
            <Typography
              sx={{ 
                fontSize: { xs: "40px", md: "52px" }, 
                fontWeight: "bold", 
                color: "#62a7f8",
                lineHeight: 1.1,
                mb: 3,
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.7s ease-out",
                transitionDelay: "0.4s",
              }}
            >
              Password
            </Typography>
            <Typography sx={{ 
              color: "rgba(255,255,255,0.9)", 
              fontSize: "18px",
              lineHeight: 1.6,
              maxWidth: "500px",
              transform: isLoaded ? "translateY(0)" : "translateY(20px)",
              opacity: isLoaded ? 1 : 0,
              transition: "all 0.7s ease-out",
              transitionDelay: "0.5s",
            }}>
              Secure access to your Talent IQ account. We'll help you get back in quickly and safely.
            </Typography>
          </Box>

          {/* Security Features */}
          <Box sx={{ 
            display: "flex", 
            gap: { xs: 3, md: 6 },
            flexWrap: "wrap",
            transform: isLoaded ? "translateY(0)" : "translateY(30px)",
            opacity: isLoaded ? 1 : 0,
            transition: "all 0.8s ease-out",
            transitionDelay: "0.6s",
          }}>
            <Box
              sx={{
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.7s",
              }}
            >
              <Typography
                sx={{ fontSize: "28px", fontWeight: "bold", color: "#62a7f8" }}
              >
                🔒
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                Secure OTP
              </Typography>
            </Box>
            <Box
              sx={{
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.8s",
              }}
            >
              <Typography
                sx={{ fontSize: "28px", fontWeight: "bold", color: "#62a7f8" }}
              >
                ⚡
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                Instant Delivery
              </Typography>
            </Box>
            <Box
              sx={{
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.9s",
              }}
            >
              <Typography
                sx={{ fontSize: "28px", fontWeight: "bold", color: "#62a7f8" }}
              >
                🛡️
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                Protected
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Right Side - Forgot Password Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            transform: isLoaded ? "translateX(0)" : "translateX(50px)",
            opacity: isLoaded ? 1 : 0,
            transition: "all 0.8s ease-out",
            transitionDelay: "0.3s",
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              p: { xs: 3, md: 5 },
              width: "100%",
              maxWidth: "450px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              transform: isLoaded ? "translateY(0) scale(1)" : "translateY(30px) scale(0.95)",
              opacity: isLoaded ? 1 : 0,
              transition: "all 0.6s ease-out",
              transitionDelay: "0.5s",
            }}
          >
            {/* Back Button */}
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToLogin}
              sx={{
                color: "#62a7f8",
                textTransform: "none",
                fontWeight: 500,
                mb: 2,
                pl: 0,
                "&:hover": {
                  backgroundColor: "rgba(98, 167, 248, 0.1)",
                },
                transform: isLoaded ? "translateX(0)" : "translateX(-20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.5s ease-out",
                transitionDelay: "0.6s",
              }}
            >
              Back to Login
            </Button>

            <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box
                  sx={{
                    width: "60px",
                    height: "60px",
                    backgroundColor: "rgba(98, 167, 248, 0.1)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                    transform: isLoaded ? "scale(1)" : "scale(0)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "0.7s",
                  }}
                >
                  <Email sx={{ color: "#62a7f8", fontSize: 30 }} />
                </Box>
                <Typography
                  sx={{ 
                    fontSize: "28px", 
                    fontWeight: 600, 
                    color: "#2b4660",
                    mb: 1,
                    transform: isLoaded ? "translateY(0)" : "translateY(-10px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "0.8s",
                  }}
                >
                  Forgot Password?
                </Typography>
                <Typography
                  sx={{ 
                    fontSize: "14px", 
                    fontWeight: 400, 
                    color: "#64748b",
                    transform: isLoaded ? "translateY(0)" : "translateY(-10px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "0.9s",
                  }}
                >
                  No worries! Enter your email and we'll send OTP to reset your password.
                </Typography>
              </Box>

              {/* Email Field */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1, 
                  mb: 1,
                  transform: isLoaded ? "translateX(0)" : "translateX(-20px)",
                  opacity: isLoaded ? 1 : 0,
                  transition: "all 0.5s ease-out",
                  transitionDelay: "1s",
                }}>
                  <Email sx={{ color: "#64748b", fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 500, color: "#2b4660" }}>
                    Email Address <span style={{ color: "red" }}>*</span>
                  </Typography>
                </Box>
                <Controller
                  name="email"
                  control={control}
                  rules={{ required: "Email is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="medium"
                      placeholder="name@example.com"
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          "&:hover fieldset": {
                            borderColor: "#62a7f8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#62a7f8",
                            borderWidth: "2px",
                          },
                          transform: isLoaded ? "translateY(0)" : "translateY(10px)",
                          opacity: isLoaded ? 1 : 0,
                          transition: "all 0.5s ease-out",
                          transitionDelay: "1.1s",
                        },
                      }}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                className="gradient-button"
                fullWidth
              >
                {loading ? "Sending OTP..." : "Send OTP"}
              </Button>

              {/* Back to Login */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  transform: isLoaded ? "translateY(0)" : "translateY(10px)",
                  opacity: isLoaded ? 1 : 0,
                  transition: "all 0.5s ease-out",
                  transitionDelay: "1.3s",
                  mt:2
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Remember your password?
                </Typography>
                <Typography
                  onClick={handleBackToLogin}
                  sx={{
                    cursor: "pointer",
                    marginLeft: "8px",
                    color: "#62a7f8",
                    fontSize: "14px",
                    fontWeight: "600",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Login here
                </Typography>
              </Box>
            </form>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ForgotPassword;
